import styled from "styled-components"

import {
  Select,
  spacing,
  Text,
  TextArea,
  TextField,
  TSelectOptions,
} from "@minuthq/meatball-ui-react"
import { setHours, startOfHour } from "date-fns"
import { FormikProps } from "formik"

import { HomesDropdown } from "src/components/Dropdown/HomesDropdown"
import { TMaybeHome } from "src/data/homes/types/homeTypes"
import { useTaskTypeLabels } from "src/data/taskManagement/hooks/useFetchTaskTableData"
import { isUnassignedOptionId } from "src/data/taskManagement/taskManagementFilters"
import {
  TTaskPostBody,
  TTaskType,
} from "src/data/taskManagement/taskManagementTypes"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { MDateTimePicker } from "src/ui/MDateTimePicker/MDateTimePicker"

import { AssigneeCombobox } from "./AssigneeCombobox"

export function TaskForm({
  form,
  homes,
}: {
  form: FormikProps<TTaskPostBody>
  homes?: TMaybeHome[]
}) {
  const { t, langKeys } = useTranslate()
  const user = useGetUser()

  const taskTypes = useTaskTypeLabels()

  const homeOption = homes?.find((h) => h?.home_id === form.values.home_id)

  const taskCategory: TSelectOptions<TTaskType> = Object.entries(taskTypes).map(
    ([type, label]) => ({
      label,
      value: type as TTaskType,
    })
  )

  return (
    <FormBox>
      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {`${t(langKeys.task_management_task_type)} *`}
        </Text>
        <Select
          value={form.values.category}
          options={[
            {
              label: t(langKeys.task_management_select_a_task),
              value: "",
              disabled: true,
            },
            ...taskCategory,
          ]}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              category: value as TTaskType,
            }))
          }}
          required
        />
      </div>

      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {`${t(langKeys.home)} *`}
        </Text>

        <HomesDropdown
          value={homeOption ?? null}
          onSelect={(home) => {
            form.setValues((prev) => ({
              ...prev,
              home_id: home?.home_id,
            }))
          }}
          required={true}
          hideLabel={true}
        />
      </div>

      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {`${t(langKeys.task_management_task_title)} *`}
        </Text>
        <TextField
          value={form.values.title}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              title: value,
            }))
          }}
          required
        />
      </div>

      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {t(langKeys.task_management_task_description)}
        </Text>
        <TextArea
          value={form.values.description ?? ""}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              description: value,
            }))
          }}
          minRows={3}
          maxRows={5}
        />
      </div>

      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {t(langKeys.task_management_task_due_date)}
        </Text>

        <MDateTimePicker
          timePickerInputProps={{
            clockType: user.clock_type,
          }}
          initialDate={
            form.values.time_window.due_date
              ? new Date(form.values.time_window.due_date)
              : startOfHour(setHours(new Date(), 12))
          }
          onChange={(value) => {
            form.setValues((prev) => {
              if (!value) {
                return {
                  ...prev,
                }
              }

              return {
                ...prev,
                time_window: {
                  ...prev.time_window,
                  due_date: value.toISOString(),
                },
              }
            })
          }}
          required
        />
      </div>

      <div>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {t(langKeys.task_management_task_assign_to)}
        </Text>

        <AssigneeCombobox
          initialValue={form.values.assigned_to?.assignee_id ?? ""}
          onChange={(value) => {
            if (isUnassignedOptionId(value)) {
              form.setValues((prev) => ({
                ...prev,
                assigned_to: { type: "unassigned" },
              }))
            } else {
              form.setValues((prev) => ({
                ...prev,
                assigned_to: {
                  type: "member",
                  assignee_id: value,
                },
              }))
            }
          }}
        />
      </div>
    </FormBox>
  )
}

const FormBox = styled.div`
  display: grid;
  gap: ${spacing.L};
`
